import { useEffect, useState } from 'react';
import { useMoralisQuery } from 'react-moralis';
import { useBoolean } from '@chakra-ui/react';
import useDidMountEffect from '../../utils/hooks/effects/didMount';
import { ethers } from 'ethers';
import { parseSocials } from '../../utils/parser';
import { Color } from '../../theme/color';

export interface ISocials {
	discord: string;
	facebook: string;
	telegram: string;
	twitch: string;
	youtube: string;
	twitter: string;
}

export interface IUser {
	address: string /** lowercase */;
	nickname: string;
	avatar: string;
	color: string;
	description: string;
	socials: ISocials;
	verified: boolean;
}

export const emptyUser: IUser = {
	address: ethers.constants.AddressZero,
	nickname: '',
	avatar: '',
	color: Color.ACTION,
	description: '',
	socials: { discord: '', facebook: '', telegram: '', twitch: '', twitter: '', youtube: '' },
	verified: false,
};

const useUser = () => {
	const [isLoading, setIsLoading] = useBoolean(true);
	const [dataId, setDataId] = useState('');
	const [data, setData] = useState(emptyUser);

	const fetch = (id?: string) => {
		setDataId(!id ? '' : id.toLowerCase());
	};

	const query = useMoralisQuery('GamerProfile', query => query.equalTo('address', dataId), [dataId], {
		autoFetch: false,
	});

	useDidMountEffect(() => {
		setIsLoading.on();

		void query.fetch();
	}, [dataId]);

	useDidMountEffect(() => {
		if (query.isLoading || query.isFetching) {
			return;
		}

		setIsLoading.off();
	}, [query.isLoading, query.isFetching]);

	/** Get user or null (only a single unique user is ever fetched as
	 * `address` is the collection's document identifier) */
	useEffect(() => {
		if (query.data.length) {
			const user = query.data.map(user => user.attributes as IUser & { socials: string })[0];

			const { socials, ...payloadRest } = user;
			const socialsParsed = parseSocials(socials);

			setData({ socials: socialsParsed, ...payloadRest });
		} else {
			setData({ ...emptyUser, address: dataId });
		}
	}, [query.data]);

	return { isLoading, data, fetch };
};

export default useUser;
